import { Builder } from '@builder.io/react';
import React from 'react';

import VideoStockItemCard from '../../../app/Search/components/card/VideoStockItemCard';
import {
  frameRates as frameRateOptions,
  getCategoriesForContentType,
  contentTypes,
  qualities as videoResolutionOptions,
} from '../../../app/Search/entities/VideoSearchFilterOptions';
import {
  defaultSortOptions,
  stockItemCardResultOptions,
  videoSortOptions,
} from '../../constants';
import {
  useVideoGridContentFetching,
  type ContentFetchingParams,
} from '../../hooks/useVideoGridContentFetching';
import { useVideoGridSchema } from '../../hooks/useVideoGridSchema';
import { normalizeStockItems } from '../../utils/contentUtils';
import headerAlignmentFontInputs, {
  Alignment,
  FontFamily,
} from '../../utils/headerAlignmentFontInputs';
import CallToAction, {
  callToActionInputs,
  type CallToActionProps,
} from '../elements/CallToAction.builder';
import BuilderSection from '../layouts/BuilderSection';
import StockItemCardLoader from '../loading-skeletons/StockItemCardLoader';

interface VideoContentGridProps {
  customContent?: any;
  category: string;
  frameRates: string[];
  heading: string;
  mediaType: string;
  minQualityScore?: number;
  numResults: string;
  resolution: string;
  searchTerm: string;
  sort: string;
  fontFamily: FontFamily;
  headingAlignment?: Alignment;
  callToAction?: CallToActionProps;
  showCallToAction: boolean;
  contentMode?: 'search' | 'collection' | 'mixed';
  galleryName?: string;
}

const VideoContentGrid = (props: VideoContentGridProps) => {
  const {
    customContent,
    category,
    frameRates,
    heading,
    mediaType,
    minQualityScore,
    numResults,
    resolution,
    searchTerm,
    sort,
    callToAction,
    showCallToAction,
    fontFamily,
    headingAlignment = Alignment.Left,
    contentMode = 'search',
    galleryName = '',
  } = props;

  const hookParams: ContentFetchingParams = {
    category: mediaType === 'all-videos' ? undefined : category,
    mediaType,
    frameRates,
    searchTerm,
    sort,
    minQualityScore,
    resolution,
    galleryName,
    numResults,
    contentMode,
    customContent: customContent ? normalizeStockItems(customContent) : [],
    builderIsEditing: !!Builder.isEditing,
  };

  const {
    content: displayContent,
    loading: displayLoading,
    error,
  } = useVideoGridContentFetching(hookParams);

  useVideoGridSchema({
    heading,
    videos: displayContent || [],
    contentMode,
    category,
    searchTerm,
  });

  const parsedNumResults = parseInt(numResults, 10);

  return (
    <BuilderSection
      heading={heading}
      headingClassNames={`${headingAlignment} ${fontFamily} text-5xl text-gray-900 font-semibold`}
    >
      <div className="w-full space-y-6">
        <div className="stock-item-group-wrapper card-design leading">
          {Builder.isEditing &&
            (contentMode === 'collection' || contentMode === 'mixed') &&
            error && (
              <div
                style={{
                  background: '#fee',
                  color: '#b00',
                  padding: '12px',
                  borderRadius: '6px',
                  marginBottom: '12px',
                  fontWeight: 'bold',
                  textAlign: 'center',
                  zIndex: 10,
                }}
              >
                Invalid collection selected! This error only shows on builder
                editing mode.
              </div>
            )}
          {displayLoading ? (
            <div className="grid grid-cols-1 gap-4">
              <div className="grid gap-6 new-card-grid">
                {[...Array(parsedNumResults)].map((_, index) => (
                  <StockItemCardLoader key={`video-card-loader-${index}`} />
                ))}
              </div>
            </div>
          ) : displayContent?.length < 1 ? (
            <div>No results found{error ? `: ${error}` : ''}</div>
          ) : (
            <div className="stock-item-v2 grid grid-cols-1 gap-4">
              <div className="grid gap-6 new-card-grid">
                {displayContent.map((item, index) => (
                  <VideoStockItemCard
                    {...item}
                    classNames={['m-0', 'p-0']}
                    containerResponsiveClassNames={[]}
                    isCollapsedStockItemLoadMoreCard={false}
                    shouldInvertControlsVisibility={true}
                    thumbnailClassNames="overflow-hidden rounded-lg"
                    key={`video-stock-item-card-${item.stockItem.id || index}`}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
        <CallToAction {...callToAction} showCallToAction={showCallToAction} />
      </div>
    </BuilderSection>
  );
};

Builder.registerComponent(VideoContentGrid, {
  name: 'Video Content Grid',
  inputs: [
    {
      name: 'heading',
      type: 'string',
      required: true,
      defaultValue: 'Browse Videos',
    },
    {
      name: 'numResults',
      friendlyName: 'Number of Results',
      type: 'string',
      enum: stockItemCardResultOptions,
      defaultValue: '9',
      required: true,
    },
    {
      name: 'contentMode',
      type: 'string',
      enum: [
        { label: 'Search', value: 'search' },
        { label: 'Collection', value: 'collection' },
        { label: 'Mixed', value: 'mixed' },
      ],
      defaultValue: 'search',
      required: true,
      friendlyName: 'Content Mode',
      onChange:
        "if (options.get('contentMode') === 'collection') { options.set('searchTerm', ''); options.set('category', ''); options.set('sort', 'most_relevant'); options.set('minQualityScore', null); options.set('resolution', '4k'); options.set('frameRate', []); } if (options.get('contentMode') === 'search') { options.set('galleryName', ''); }",
    },
    {
      name: 'galleryName',
      type: 'string',
      defaultValue: '',
      required: true,
      showIf: "options.get('contentMode') !== 'search'",
      friendlyName: 'Collection Slug (URI)',
      helperText:
        'Enter the collection slug/URI (the part after /video/collections/ in the URL)',
      regex: {
        pattern: '^(?!/).+$',
        options: '',
        message:
          'Collection Slug (galleryName) is required and should not start with a "/". Please enter only the slug part after /video/collections/.',
      },
    },
    {
      name: 'mediaType',
      type: 'string',
      required: true,
      enum: contentTypes,
      defaultValue: 'all-videos',
    },
    {
      name: 'searchTerm',
      type: 'string',
      defaultValue: '',
      showIf: "options.get('contentMode') !== 'collection'",
    },
    {
      name: 'resolution',
      type: 'string',
      enum: videoResolutionOptions,
      defaultValue: '',
      showIf: "options.get('contentMode') !== 'collection'",
    },
    {
      name: 'frameRate',
      type: 'list',
      defaultValue: [],
      subFields: [
        {
          name: 'rate',
          type: 'string',
          enum: frameRateOptions,
        },
      ],
      showIf: "options.get('contentMode') !== 'collection'",
    },
    {
      name: 'category',
      type: 'string',
      showIf:
        "options.get('mediaType') === 'footage' && options.get('contentMode') !== 'collection'",
      enum: getCategoriesForContentType('footage'),
      defaultValue: '',
    },
    {
      name: 'category',
      type: 'string',
      showIf:
        "options.get('mediaType') === 'motion-backgrounds' && options.get('contentMode') !== 'collection'",
      enum: getCategoriesForContentType('motion-backgrounds'),
      defaultValue: '',
    },
    {
      name: 'category',
      type: 'string',
      showIf:
        "options.get('mediaType') === 'templates' && options.get('contentMode') !== 'collection'",
      enum: getCategoriesForContentType('templates'),
      defaultValue: '',
    },
    {
      name: 'sort',
      type: 'string',
      defaultValue: 'most_relevant',
      required: true,
      enum: [...defaultSortOptions, ...videoSortOptions],
      showIf: "options.get('contentMode') !== 'collection'",
    },
    {
      name: 'minQualityScore',
      type: 'number',
      defaultValue: null,
      min: 0,
      max: 1,
      showIf: "options.get('contentMode') !== 'collection'",
    },
    ...headerAlignmentFontInputs,
    ...callToActionInputs,
  ],
});
